/**
 * 全局常量：题型枚举、状态枚举、错因标签。
 *
 * 本文件是「唯一真源」：前端、后端、命令行工具一律从这里引入，
 * 不允许在别处重复定义题型或状态字面量（旧项目题型枚举在 4 处重复定义的教训）。
 */
export const CATEGORIES = ['P1', 'P2', 'P3'];
/* 说明：曾经有 frequency（高频/中频/低频）分类，已按需求移除，
   因为实际使用时不需要按频率筛选；数据库里的对应字段也已删除（迁移 0003）。 */
/** 题型枚举。命名可改，但前后端必须共用这一份。 */
export const QUESTION_KINDS = [
    'tfng',
    'ynng',
    'single_choice',
    'multi_choice',
    'matching',
    'matching_headings',
    'summary_completion',
    'sentence_completion',
    'table_completion',
    'flow_chart_completion',
    'diagram_labeling',
    'short_answer',
];
/** 需要从选项池里选答案的题型（答案必须是选项 key）。 */
export const CHOICE_KINDS = [
    'single_choice',
    'multi_choice',
    'matching',
    'matching_headings',
];
/** 需要显式声明 reuseOptions 的题型（见 PLAN 4.5 规则 5）。 */
export const REUSE_OPTION_KINDS = ['matching', 'matching_headings'];
/** 填空类题型（题干里用 <strong>N</strong> ______ 表示空位）。 */
export const COMPLETION_KINDS = [
    'summary_completion',
    'sentence_completion',
    'table_completion',
    'flow_chart_completion',
    'diagram_labeling',
];
/** 固定选项的题型（内容里可以不写 options，渲染器按此生成）。 */
export const FIXED_OPTIONS = {
    tfng: [
        { key: 'TRUE', text: 'TRUE' },
        { key: 'FALSE', text: 'FALSE' },
        { key: 'NOT GIVEN', text: 'NOT GIVEN' },
    ],
    ynng: [
        { key: 'YES', text: 'YES' },
        { key: 'NO', text: 'NO' },
        { key: 'NOT GIVEN', text: 'NOT GIVEN' },
    ],
};
export const KIND_LABELS = {
    tfng: '判断题（T/F/NG）',
    ynng: '判断题（Y/N/NG）',
    single_choice: '单选题',
    multi_choice: '多选题',
    matching: '匹配题',
    matching_headings: '段落标题',
    summary_completion: '摘要填空',
    sentence_completion: '句子填空',
    table_completion: '表格填空',
    flow_chart_completion: '流程图填空',
    diagram_labeling: '图表标注',
    short_answer: '简答题',
};
export const EXAM_STATUSES = ['draft', 'published', 'archived'];
export const ATTEMPT_STATUSES = ['in_progress', 'submitted', 'abandoned'];
export const MISTAKE_STATUSES = ['new', 'reviewing', 'mastered'];
export const MISTAKE_STATUS_LABELS = {
    new: '未处理',
    reviewing: '复习中',
    mastered: '已掌握',
};
/** 6 个固定错因标签（外加自由标签）。 */
export const ERROR_TAGS = [
    'synonym_miss',
    'location_error',
    'logic_error',
    'vocabulary',
    'detail_miss',
    'syntax',
];
export const ERROR_TAG_LABELS = {
    synonym_miss: '同义替换失误',
    location_error: '定位偏差',
    logic_error: 'T/F/NG 逻辑误判',
    vocabulary: '词汇障碍',
    detail_miss: '细节遗漏',
    syntax: '复杂句理解',
};
export const SCHEMA_VERSION = 'leits.reading.exam/1';
/* ------------------------------------------------------------------ *
 * 标注（高亮与笔记）
 * ------------------------------------------------------------------ */
/**
 * 标注范围：**练习页只允许标注阅读文章（passage.blocks），答题区一律禁止**。
 *
 * 依据：真实机考从 2026-03-25 起不支持对试卷高亮，所以练习时不提供对题干的标注能力，
 * 避免养成考场用不上的习惯。服务端按 surface 校验：surface='practice' 时锚点必须落在
 * passage 的段落上，因此**结构上不可能**给题目/选项/答题卡写标注。
 *
 * 精读页（surface='close-reading'）是另一个界面：它的目的就是逐句拆解，所以题干与选项
 * 也允许标注。两个界面共用同一张 annotations 表，用 anchorKind 区分锚点落点。
 */
export const ANNOTATION_SCOPE_BY_SURFACE = {
    practice: 'passage-only',
    'close-reading': 'passage-and-questions',
};
export const ANNOTATION_SURFACES = ['practice', 'close-reading'];
/** 兼容旧调用点：默认界面（练习页）的标注范围。 */
export const ANNOTATION_SCOPE = ANNOTATION_SCOPE_BY_SURFACE.practice;
/* ------------------------------------------------------------------ *
 * 标注锚点
 * ------------------------------------------------------------------ */
/**
 * 锚点落点：
 *   · passage  —— anchorId 是 passage.blocks 里的段落 id（`p-1`、`p-A`…）
 *   · question —— anchorId 是题目 id（`q14`），或「题目 id#选项 key」（`q14#A`）表示某一个选项
 *
 * 两种锚点都用 `[data-anchor-kind][data-block-id]` 标在 DOM 上，
 * 因此题目锚点不可能误落到同名的段落元素上。
 */
export const ANCHOR_KINDS = ['passage', 'question'];
/** 题库里题目的 id 与段落 id 前缀不同（`q…` / `p-…`），这里再拼一遍是为了让锚点自解释。 */
export function optionAnchorId(questionId, optionKey) {
    return `${questionId}#${optionKey}`;
}
export function splitAnchorId(anchorId) {
    const index = anchorId.indexOf('#');
    if (index === -1)
        return { questionId: anchorId, optionKey: null };
    return { questionId: anchorId.slice(0, index), optionKey: anchorId.slice(index + 1) };
}
export const ANNOTATION_KINDS = ['highlight', 'note'];
/** 高亮颜色。只用于标记正文内容，不计入界面强调色（属于内容标记而非界面装饰）。 */
export const HIGHLIGHT_COLORS = ['yellow', 'green', 'blue', 'pink'];
export const HIGHLIGHT_COLOR_LABELS = {
    yellow: '黄',
    green: '绿',
    blue: '蓝',
    pink: '粉',
};
/** 单次标注是否合法：高亮必须有颜色，笔记必须有内容。 */
export function isValidAnnotation(kind, color, note) {
    if (kind === 'highlight')
        return Boolean(color);
    return Boolean(note && note.trim().length > 0);
}
//# sourceMappingURL=constants.js.map